/**
 * RIFF/WAVE container inspection (TES-2236).
 *
 * The catalog serves every sound as `LockChime.wav`, and
 * `app/api/sounds/[id]/download-file/route.ts` streams the stored bytes
 * verbatim. So the container the bytes actually are is the container the owner
 * copies into `Boombox/`. TES-2154 found 50 rows that were MP3 streams under a
 * `.wav` filename: browsers and CoreAudio sniff by content and previewed them
 * happily, the car played nothing, and no telemetry anywhere noticed — an
 * install failure is invisible to a playback error stream.
 *
 * This module is the single place that answers "are these bytes a WAV?".
 * `scripts/audit-wav-containers.ts` uses it to name existing rows;
 * `assertRiffWave` is the ingest gate that stops new ones being written.
 *
 * Pure buffer arithmetic, no I/O and no Node-only APIs, so the ingest scripts
 * and the app can share it.
 */

export type WavContainerFinding =
  /** Not a RIFF/WAVE file at all — an MP3, Ogg, FLAC or anything else. */
  | 'not-riff'
  /**
   * A chunk sits between `fmt ` and `data`. RIFF-legal, decodes on desktop, and
   * rejected outright by iOS WebKit with MEDIA_ERR_SRC_NOT_SUPPORTED.
   */
  | 'chunk-before-data'
  /** Not 44.1kHz/16-bit PCM, i.e. off the Tesla audio spec. */
  | 'off-spec-fmt'
  /** Too few bytes to reach a verdict. */
  | 'unreadable'

export interface WavContainerInspection {
  /** Empty when the bytes are a clean, on-spec WAV. */
  findings: WavContainerFinding[]
  /** Chunk ids in file order, so a novel layout is visible without a re-read. */
  chunks: string[]
  sampleRate?: number
  bitsPerSample?: number
  channels?: number
  /** Human-readable note, set when a finding needs one. */
  detail?: string
}

/** The Tesla lock-sound spec; see lib/tesla-facts.ts. */
const SPEC_SAMPLE_RATE = 44_100
const SPEC_BITS_PER_SAMPLE = 16

/** RIFF header (12 bytes) + the smallest useful `fmt ` chunk. */
const MIN_HEADER_BYTES = 12

function ascii(buf: Uint8Array, start: number, end: number): string {
  let out = ''
  for (let i = start; i < end && i < buf.length; i++) out += String.fromCharCode(buf[i])
  return out
}

function readUInt32LE(buf: Uint8Array, offset: number): number {
  return (buf[offset] | (buf[offset + 1] << 8) | (buf[offset + 2] << 16) | (buf[offset + 3] << 24)) >>> 0
}

function readUInt16LE(buf: Uint8Array, offset: number): number {
  return buf[offset] | (buf[offset + 1] << 8)
}

/** Name the container a non-WAV file actually is, for the rejection message. */
export function describeContainer(buf: Uint8Array): string {
  const magic = ascii(buf, 0, 4)
  if (magic === 'RIFF') return ascii(buf, 8, 12) === 'WAVE' ? 'RIFF/WAVE' : `RIFF/${ascii(buf, 8, 12)}`
  if (ascii(buf, 0, 3) === 'ID3') return 'MP3 (ID3 header)'
  // A bare MPEG audio frame: 11 sync bits, no ID3 tag. 43 of TES-2154's 50 rows
  // carried ID3, but the scrapers also produce these.
  if (buf.length >= 2 && buf[0] === 0xff && (buf[1] & 0xe0) === 0xe0) return 'MP3 (MPEG frame sync)'
  if (magic === 'OggS') return 'Ogg'
  if (magic === 'fLaC') return 'FLAC'
  if (magic === 'FORM') return 'AIFF'
  if (ascii(buf, 4, 8) === 'ftyp') return 'MP4/M4A'
  return `unknown magic ${JSON.stringify(magic)}`
}

/**
 * Walk the RIFF chunk list.
 *
 * Safe on a truncated read: the audit fetches only a header window, and every
 * finding here is about the chunks that precede `data`, which fit in it. A
 * caller holding the whole file gets the same answer.
 */
export function inspectWavContainer(buf: Uint8Array): WavContainerInspection {
  if (buf.length < MIN_HEADER_BYTES) {
    return { findings: ['unreadable'], chunks: [], detail: `only ${buf.length} bytes` }
  }

  if (ascii(buf, 0, 4) !== 'RIFF' || ascii(buf, 8, 12) !== 'WAVE') {
    return {
      findings: ['not-riff'],
      chunks: [],
      detail: `${describeContainer(buf)} served with a .wav path`,
    }
  }

  const chunks: string[] = []
  const findings: WavContainerFinding[] = []
  let sampleRate: number | undefined
  let bitsPerSample: number | undefined
  let channels: number | undefined
  let sawFmt = false
  let offset = 12

  while (offset + 8 <= buf.length) {
    const id = ascii(buf, offset, offset + 4)
    const size = readUInt32LE(buf, offset + 4)
    chunks.push(id)

    if (id === 'fmt ' && offset + 8 + 16 <= buf.length) {
      sawFmt = true
      channels = readUInt16LE(buf, offset + 10)
      sampleRate = readUInt32LE(buf, offset + 12)
      bitsPerSample = readUInt16LE(buf, offset + 22)
    }

    if (id === 'data') break

    // `LIST` is the one the scrape-time ffmpeg remux injects; anything else in
    // that slot is equally suspect to iOS, so report it by name rather than
    // allow-listing a single id.
    if (sawFmt && id !== 'fmt ') findings.push('chunk-before-data')

    // Chunks are word-aligned: an odd size carries a pad byte.
    offset += 8 + size + (size % 2)
  }

  if (sampleRate !== SPEC_SAMPLE_RATE || bitsPerSample !== SPEC_BITS_PER_SAMPLE) {
    findings.push('off-spec-fmt')
  }

  return {
    findings: [...new Set(findings)],
    chunks,
    sampleRate,
    bitsPerSample,
    channels,
  }
}

export function isRiffWave(buf: Uint8Array): boolean {
  return !inspectWavContainer(buf).findings.includes('not-riff')
}

/** Thrown by `assertRiffWave`. Carries the inspection so callers can log it. */
export class WavContainerError extends Error {
  readonly inspection: WavContainerInspection

  constructor(message: string, inspection: WavContainerInspection) {
    super(message)
    this.name = 'WavContainerError'
    this.inspection = inspection
  }
}

/**
 * The ingest gate. Throws unless `bytes` is genuinely a RIFF/WAVE file.
 *
 * Call this immediately before writing audio to the `audio-files` bucket —
 * every one of TES-2154's 50 rows was a write that would have failed here. It
 * is deliberately cheap (a header walk, no decode) so it can sit on the hot
 * path of a 500-file batch, and deliberately fatal rather than corrective: a
 * transcode belongs in the pipeline step that owns the file, not in a guard.
 *
 * Off-spec sample rate / bit depth is returned in the inspection but does not
 * throw. Those files install and play; they are TES-2003's problem, not this
 * gate's, and failing them here would reject legitimate historical uploads.
 */
export function assertRiffWave(bytes: Uint8Array, label: string): WavContainerInspection {
  const inspection = inspectWavContainer(bytes)
  if (inspection.findings.includes('not-riff') || inspection.findings.includes('unreadable')) {
    throw new WavContainerError(
      `Refusing to store non-WAV bytes at "${label}": ${inspection.detail ?? 'unreadable'}. ` +
        'Tesla plays the stored bytes verbatim as LockChime.wav. Transcode to ' +
        `${SPEC_SAMPLE_RATE}Hz/${SPEC_BITS_PER_SAMPLE}-bit PCM WAV before uploading.`,
      inspection
    )
  }
  return inspection
}
